import React, { useEffect, useState } from 'react';
import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { Table } from '@consta/uikit/Table';
import classes from './ComplitedMonitoringContent.module.scss';
import { useNavigate } from 'react-router-dom';
import { useLazyGetReportQuery } from '../../../services/reportService';
import { Report } from '../../../services/types/TReportServices';

const ComplitedMonitoringContent: React.FC = () => {
  const navigate = useNavigate();
  const [triggerGetReport, { data: reportData, isLoading, error }] = useLazyGetReportQuery();
  const [reports, setReports] = useState<Report[]>([]);

  // Загружаем отчеты при монтировании компонента
  useEffect(() => {
    triggerGetReport({
      limit: 100,
      offset: 0,
      ordering: '-created_at'
    });
  }, [triggerGetReport]);

  // Обновляем локальное состояние при получении данных
  useEffect(() => {
    if (reportData?.results) {
      setReports(reportData.results);
    }
  }, [reportData]);

  const handleOpenMonitoring = (reportUuid: string) => {
    if (reportUuid) {
      navigate(`/report/${reportUuid}`);
    }
  };

  // Функция для преобразования даты в нужный формат
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return dateString;
    }
  };

  // Функция для получения названия проекта
  const getProjectName = (report: Report) => {
    return report?.project_info?.name || 'Не указано';
  };

  // Функция для получения номера проекта
  const getProjectNumber = (report: Report) => {
    return report?.project_info?.number || 'Не указан';
  };

  // Функция для получения компании
  const getCompanyName = (report: Report) => {
    return report?.project_info?.company?.name || 'Не указана';
  };

  // Функция для получения количества скважин
  const getWellsCount = (report: Report) => {
    return report?.project_info?.wells?.length || 0;
  };

  // Функция для получения количества месторождений
  const getFieldsCount = (report: Report) => {
    return report?.project_info?.fields?.length || 0;
  };

  // Функция для получения периода проекта
  const getProjectPeriod = (report: Report) => {
    const start = report?.project_info?.start_date;
    const end = report?.project_info?.end_date;
    
    if (start && end) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    } else if (start) {
      return `с ${formatDate(start)}`;
    } else if (end) {
      return `до ${formatDate(end)}`;
    }
    return 'Не указан';
  };

  // Функция для обработки клика по номеру проекта
  const handleProjectNumberClick = (reportUuid: string) => {
    handleOpenMonitoring(reportUuid);
  };

  // Колонки таблицы с адаптивными ширинами
  const columns = [
    {
      title: '№',
      accessor: 'rowNumber',
      width: 60,
      renderCell: (row: any, rowIndex: number) => {
        const index = typeof rowIndex === 'number' ? rowIndex : 
                    row.rowIndex || 
                    tableRows.findIndex((r:any) => r.id === row.id);
        return (
          <div >
            {index + 1}
          </div>
        );
      }
    },
    {
      title: '№ проекта',
      accessor: 'projectNumber',
      sortable: true,
      renderCell: (row: any) => (
        <Text 
          weight="semibold" 
          className={classes.projectNumberLink}
          onClick={() => handleProjectNumberClick(row.reportUuid)}
        >
          {row.projectNumber}
        </Text>
      )
    },
    {
      title: 'Наименование проекта',
      accessor: 'projectName',
      sortable: true,
      renderCell: (row: any) => (
        <Text size="s">{row.projectName}</Text>
      )
    },
    {
      title: 'Компания',
      accessor: 'company',
      sortable: true,
      renderCell: (row: any) => (
        <Text size="s">{row.company}</Text>
      )
    },
    {
      title: 'Период',
      accessor: 'period',
      sortable: true,
      renderCell: (row: any) => (
        <Text size="xs">{row.period}</Text>
      )
    },
    {
      title: 'Дата создания',
      accessor: 'createdDate',
      sortable: true,
      renderCell: (row: any) => (
        <Text size="xs">{row.createdDate}</Text>
      )
    },
    {
      title: 'Категория',
      accessor: 'category',
      sortable: true,
      renderCell: (row: any) => (
        <Text size="xs" view="secondary">{row.category}</Text>
      )
    },
  ];

  // Преобразование данных для таблицы
  const tableRows = reports.map(report => ({
    id: report.uuid || report.id.toString(),
    projectNumber: getProjectNumber(report),
    projectName: getProjectName(report),
    company: getCompanyName(report),
    period: getProjectPeriod(report),
    fieldsCount: getFieldsCount(report),
    wellsCount: getWellsCount(report),
    createdDate: formatDate(report.created_at),
    category: report.category || 'monitoring',
    reportUuid: report.uuid,
    report: report
  }));

  if (isLoading) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.mainContent}>
          <div className={classes.content}>
            <div className={classes.container}>
              <Text size="2xl" weight="bold" className={classes.title}>
                Завершенные мониторинги проектов
              </Text>
              <div className={classes.loadingState}>
                <Text>Загрузка отчетов...</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.mainContent}>
          <div className={classes.content}>
            <div className={classes.container}>
              <Text size="2xl" weight="bold" className={classes.title}>
                Завершенные мониторинги проектов
              </Text>
              <div className={classes.errorState}>
                <Text view="warning">Ошибка загрузки отчетов</Text>
                <Text size="s" view="secondary">
                  Не удалось загрузить список завершенных мониторингов
                </Text>
                <Button 
                  size="s"
                  label="Попробовать снова" 
                  onClick={() => triggerGetReport({
                    limit: 100,
                    offset: 0,
                    ordering: '-created_at'
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <div className={classes.mainContent}>
        <div className={classes.content}>
          <div className={classes.container}>
            {/* Заголовок и статистика */}
            <div className={classes.header}>
              <Text size="2xl" weight="bold" className={classes.title}>
                Завершенные мониторинги проектов
              </Text>
              
              {reportData && (
                <div className={classes.stats}>
                  <Text size="s" view="secondary">
                    Всего отчетов: {reportData.count}
                  </Text>
                </div>
              )}
            </div>

            {/* Таблица */}
            <Card verticalSpace="m" horizontalSpace="m" className={classes.tableCard}>
              {tableRows.length > 0 ? (
                <div className={classes.tableWrapper}>
                  <Table
                    stickyHeader
                    stickyColumns={1}
                    rows={tableRows}
                    //@ts-ignore
                    columns={columns}
                    borderBetweenColumns
                    borderBetweenRows
                    verticalAlign="center"
                    size="s"
                    className={classes.table}
                  />
                </div>
              ) : (
                <div className={classes.emptyState}>
                  <Text view="secondary">Нет завершенных мониторингов</Text>
                  <Text size="s" view="secondary">
                    Завершенные отчеты по мониторингам появятся здесь
                  </Text>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplitedMonitoringContent;